import { useEffect, useState } from "react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  Instructor,
  ManagerCalendarEvent,
  ScheduledLessonRecord,
  UnscheduledLesson,
  Venue,
} from "../../../api/schemas.js";
import { isoToWallClock, shortZoneName, wallClockToIso } from "../../../lib/timezone.js";
import { DialogShell } from "../../../ui/DialogShell.js";
import { TimezoneLabels } from "./TimezoneLabels.js";

export interface LessonFormValues {
  readonly startsAtLocal: string;
  readonly instructorId: string;
  readonly venueId: string;
  readonly durationMinutes: number;
  readonly reason: string;
  readonly capacityOverrideReason: string;
  readonly requireCapacityOverride: boolean;
  readonly selectedLessonId?: string;
}

export interface LessonFormDialogProps {
  readonly open: boolean;
  readonly mode: "create" | "edit" | "reschedule";
  readonly instructors: readonly Instructor[];
  readonly venues: readonly Venue[];
  readonly event?: ManagerCalendarEvent | null;
  readonly unscheduled?: ScheduledLessonRecord | UnscheduledLesson | null;
  /** Global/deal pool of bookable unscheduled lessons for the create selector. */
  readonly unscheduledOptions?: readonly UnscheduledLesson[];
  readonly requireLessonSelection?: boolean;
  readonly initialStartsAt?: string;
  /**
   * Пояс площадки. Поле `datetime-local` не умеет зон вовсе — оно всегда о «часах на стене»,
   * и вопрос только в том, чья это стена. Стена площадки: менеджер, назначающий урок на Бали
   * из Москвы, набирает время, которое увидит клиент.
   */
  readonly timeZone: string;
  readonly initialInstructorId?: string;
  readonly initialVenueId?: string;
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (values: LessonFormValues) => void;
}

function isUnscheduledLesson(
  value: ScheduledLessonRecord | UnscheduledLesson | null | undefined,
): value is UnscheduledLesson {
  return value !== null && value !== undefined && "lessonId" in value && !("id" in value);
}

function lessonIdOf(
  value: ScheduledLessonRecord | UnscheduledLesson | null | undefined,
): string | undefined {
  if (!value) return undefined;
  if (isUnscheduledLesson(value)) return value.lessonId;
  return value.id;
}

/**
 * Подпись урока в списке. Пустые поля не заменяются словами-заглушками: строка
 * «Клиент · Курс · урок #1» ничего не различает, а в режиме сделки клиент и курс и так известны
 * из шапки — тогда остаётся то, что действительно отличает уроки друг от друга.
 */
function formatUnscheduledLabel(item: UnscheduledLesson): string {
  const progress = item.courseTotalLessons
    ? `урок ${item.lessonOrder}/${item.courseTotalLessons}`
    : `урок ${item.lessonOrder}`;
  const parts = [item.clientName, item.courseCode ?? item.courseTitle, progress].filter(
    (part): part is string => Boolean(part),
  );
  return `${parts.join(" · ")} · ${item.durationMinutes} мин`;
}

/**
 * Внутри <form> Radix рендерит скрытый нативный список, куда попадают только уже
 * смонтированные пункты. Пока список свёрнут, пунктов нет, и нативный select
 * «сбрасывает» значение в пустое, дёргая onValueChange(""). Пустое значение здесь
 * не выбирается никогда — гасим его, иначе заполненное поле обнуляется само.
 */
function keepValue(set: (value: string) => void): (value: string) => void {
  return (value) => {
    if (value) {
      set(value);
    }
  };
}

function toVenueInputValue(iso: string | undefined, timeZone: string): string {
  if (!iso) {
    const next = new Date();
    next.setMinutes(0, 0, 0);
    next.setHours(next.getHours() + 1);
    return toVenueInputValue(next.toISOString(), timeZone);
  }
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) {
    return "";
  }
  const wall = isoToWallClock(instant, timeZone);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${wall.getFullYear()}-${pad(wall.getMonth() + 1)}-${pad(wall.getDate())}T${pad(wall.getHours())}:${pad(wall.getMinutes())}`;
}

export function LessonFormDialog(props: LessonFormDialogProps) {
  const [startsAtLocal, setStartsAtLocal] = useState("");
  const [instructorId, setInstructorId] = useState("");
  const [venueId, setVenueId] = useState("");
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [reason, setReason] = useState("");
  const [capacityOverrideReason, setCapacityOverrideReason] = useState("");
  const [requireOverride, setRequireOverride] = useState(false);
  const [selectedLessonId, setSelectedLessonId] = useState("");
  /** Какой именно заполненности соответствуют поля; `null` — диалог закрыт. */
  const [initializedKey, setInitializedKey] = useState<string | null>(null);

  const options = props.unscheduledOptions;
  const optionList = options ?? [];
  const showLessonSelector =
    props.mode === "create" && (props.requireLessonSelection === true || optionList.length > 0);

  /**
   * Что именно сейчас заполняет форма. Меняется, когда диалог открывают заново, переключают
   * режим или выбирают другой урок, — и только тогда поля имеет право перезаписать.
   */
  const formKey = [
    props.mode,
    lessonIdOf(props.unscheduled) ?? "",
    props.event?.lessonId ?? "",
    props.initialStartsAt ?? "",
    props.initialInstructorId ?? "",
    props.initialVenueId ?? "",
    props.timeZone,
  ].join("|");

  /**
   * Заполнение формы — не эффект, а поправка состояния на смену пропсов, и делается она прямо
   * в рендере (React для этого случая это и предусматривает).
   *
   * Эффектом было нельзя: в списке его зависимостей стоял массив вариантов, а в режимах
   * «изменить» и «перенести» он приходит литералом `[]` — новая ссылка на каждом рендере
   * родителя. Эффект перезапускался постоянно и стирал набранное: время, площадку и причину
   * переноса, самое длинное поле формы. Сузить список зависимостей вручную значило бы соврать
   * линтеру про то, что эффект читает; поправка же читает всё здесь и сейчас.
   */
  if (!props.open) {
    if (initializedKey !== null) {
      setInitializedKey(null);
    }
  } else if (initializedKey !== formKey) {
    setInitializedKey(formKey);

    const event = props.event;
    const unscheduled = props.unscheduled;
    const initialLessonId = lessonIdOf(unscheduled) ?? optionList[0]?.lessonId ?? "";
    setSelectedLessonId(initialLessonId);

    const selectedOption =
      optionList.find((o) => o.lessonId === initialLessonId) ??
      (isUnscheduledLesson(unscheduled) ? unscheduled : null);

    setStartsAtLocal(
      toVenueInputValue(
        props.initialStartsAt ??
          event?.startTime ??
          (unscheduled && "startTime" in unscheduled
            ? (unscheduled.startTime ?? undefined)
            : undefined),
        props.timeZone,
      ),
    );
    setInstructorId(
      props.initialInstructorId ??
        event?.instructorId ??
        selectedOption?.instructorId ??
        (unscheduled && "instructorId" in unscheduled ? unscheduled.instructorId : null) ??
        props.instructors[0]?.id ??
        "",
    );
    setVenueId(
      props.initialVenueId ??
        event?.venueId ??
        (unscheduled && "venueId" in unscheduled ? unscheduled.venueId : null) ??
        props.venues[0]?.id ??
        "",
    );
    setDurationMinutes(
      event?.durationMinutes ??
        selectedOption?.durationMinutes ??
        (unscheduled && "durationMinutes" in unscheduled ? unscheduled.durationMinutes : 60) ??
        60,
    );
    setReason("");
    setCapacityOverrideReason("");
    setRequireOverride(false);
  }

  useEffect(() => {
    if (!showLessonSelector || !selectedLessonId) {
      return;
    }
    const selected = (options ?? []).find((o) => o.lessonId === selectedLessonId);
    if (!selected) {
      return;
    }
    if (selected.instructorId) {
      setInstructorId(selected.instructorId);
    }
    setDurationMinutes(selected.durationMinutes);
  }, [selectedLessonId, options, showLessonSelector]);

  const selectedVenue = props.venues.find((v) => v.id === venueId) ?? null;
  const venueTimeZone = selectedVenue?.timezone ?? props.timeZone;
  /*
   * Подписи выбранного значения задаём явно: Radix узнаёт текст пункта только после
   * первого раскрытия списка, поэтому иначе триггер остаётся пустым при значении,
   * выставленном из эффекта.
   */
  const selectedLessonLabel = (() => {
    const item = optionList.find((o) => o.lessonId === selectedLessonId);
    return item ? formatUnscheduledLabel(item) : null;
  })();
  const selectedInstructorLabel =
    props.instructors.find((i) => i.id === instructorId)?.name ?? null;
  const selectedVenueLabel = selectedVenue
    ? `${selectedVenue.name} · вмест. ${selectedVenue.capacity} · ${selectedVenue.timezone}`
    : null;
  const startsAtIso = startsAtLocal ? wallClockToIso(new Date(startsAtLocal), venueTimeZone) : "";
  const title =
    props.mode === "create"
      ? "Записать урок"
      : props.mode === "reschedule"
        ? "Перенести урок"
        : "Изменить урок";

  const canSubmit =
    !props.busy &&
    Boolean(startsAtLocal) &&
    Boolean(instructorId) &&
    Boolean(venueId) &&
    (!showLessonSelector || Boolean(selectedLessonId));

  const buildValues = (): LessonFormValues => ({
    startsAtLocal,
    instructorId,
    venueId,
    durationMinutes,
    reason,
    capacityOverrideReason,
    requireCapacityOverride: requireOverride,
    ...(showLessonSelector && selectedLessonId ? { selectedLessonId } : {}),
  });

  return (
    <DialogShell
      open={props.open}
      title={title}
      onClose={props.onClose}
      testId="lesson-form-dialog"
      footer={
        <>
          <button
            type="button"
            className="pwz-btn pwz-btn-secondary"
            onClick={props.onClose}
            disabled={props.busy}
          >
            Отмена
          </button>
          <button
            type="button"
            className="pwz-btn pwz-btn-primary"
            data-testid="lesson-form-submit"
            disabled={!canSubmit}
            onClick={() => props.onSubmit(buildValues())}
          >
            {props.busy ? "Сохранение…" : "Сохранить"}
          </button>
        </>
      }
    >
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) {
            props.onSubmit(buildValues());
          }
        }}
      >
        {showLessonSelector ? (
          <div className="space-y-1.5 text-sm">
            <Label htmlFor="field-unscheduled-lesson" className="pwz-eyebrow">
              Урок учебного плана
            </Label>
            <Select value={selectedLessonId} onValueChange={keepValue(setSelectedLessonId)}>
              <SelectTrigger
                id="field-unscheduled-lesson"
                data-testid="field-unscheduled-lesson"
                className="w-full"
                aria-label="Выбор урока учебного плана: клиент, курс и номер"
              >
                <SelectValue placeholder="В учебном плане нет свободных уроков">
                  {selectedLessonLabel}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {optionList.map((item) => (
                  <SelectItem key={item.lessonId} value={item.lessonId}>
                    {formatUnscheduledLabel(item)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-ink-600 text-xs">
              Клиент · курс · прогресс · длительность. Стоимость курса остаётся в AmoCRM.
            </p>
          </div>
        ) : null}

        {startsAtIso ? (
          <div className="border-y border-ink-200 py-3">
            <TimezoneLabels iso={startsAtIso} venueTimeZone={venueTimeZone} />
          </div>
        ) : null}

        <label className="block text-sm">
          <span className="pwz-eyebrow mb-1.5 block">
            Начало — время площадки ({shortZoneName(venueTimeZone)})
          </span>
          <input
            type="datetime-local"
            className="pwz-field pwz-readout"
            value={startsAtLocal}
            onChange={(e) => setStartsAtLocal(e.target.value)}
            required
            data-testid="field-starts-at"
          />
        </label>

        <div className="space-y-1.5 text-sm">
          <Label htmlFor="field-instructor" className="pwz-eyebrow">
            Инструктор
          </Label>
          <Select value={instructorId} onValueChange={keepValue(setInstructorId)}>
            <SelectTrigger id="field-instructor" data-testid="field-instructor" className="w-full">
              <SelectValue placeholder="— выберите —">{selectedInstructorLabel}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {props.instructors.map((i) => (
                <SelectItem key={i.id} value={i.id}>
                  {i.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5 text-sm">
          <Label htmlFor="field-venue" className="pwz-eyebrow">
            Площадка
          </Label>
          <Select value={venueId} onValueChange={keepValue(setVenueId)}>
            <SelectTrigger id="field-venue" data-testid="field-venue" className="w-full">
              <SelectValue placeholder="— выберите —">{selectedVenueLabel}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {props.venues.map((v) => (
                <SelectItem key={v.id} value={v.id}>
                  {v.name} · вмест. {v.capacity} · {v.timezone}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <label className="block text-sm">
          <span className="pwz-eyebrow mb-1.5 block">Длительность, мин</span>
          <input
            type="number"
            min={15}
            step={5}
            className="pwz-field pwz-readout"
            value={durationMinutes}
            onChange={(e) => setDurationMinutes(Number(e.target.value))}
            data-testid="field-duration"
          />
        </label>

        {props.mode === "reschedule" || props.mode === "edit" ? (
          <label className="block text-sm">
            <span className="pwz-eyebrow mb-1.5 block">Причина</span>
            <input
              type="text"
              className="pwz-field"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required={props.mode === "reschedule"}
              placeholder="Запрос клиента, погода…"
              data-testid="field-reason"
            />
          </label>
        ) : null}

        <label className="pwz-mark flex items-start gap-2.5 border-t border-ink-200 py-3 pl-3.5 text-sm">
          <input
            type="checkbox"
            className="mt-0.5 h-5 w-5 accent-ink-950"
            checked={requireOverride}
            onChange={(e) => setRequireOverride(e.target.checked)}
            data-testid="field-capacity-override-toggle"
          />
          <span className="min-w-0">
            <span className="pwz-eyebrow block">Переопределение вместимости</span>
            <span className="mt-0.5 block text-ink-600">
              Только если сервер отклонит запись из‑за вместимости. Причина обязательна.
            </span>
          </span>
        </label>

        {requireOverride ? (
          <label className="block text-sm">
            <span className="pwz-eyebrow mb-1.5 block">Причина переопределения</span>
            <input
              type="text"
              className="pwz-field border-warn-500 bg-warn-100"
              value={capacityOverrideReason}
              onChange={(e) => setCapacityOverrideReason(e.target.value)}
              required
              data-testid="field-capacity-override-reason"
              placeholder="Почему превышаем вместимость?"
            />
          </label>
        ) : null}
      </form>
    </DialogShell>
  );
}

export { formatUnscheduledLabel };
